import {createInjectionState, useEventListener} from '@vueuse/core'
import {computed, ref} from 'vue'

export interface OverlayEntry {
  id: symbol
  zIndex: number
  element: HTMLElement | null
  referenceElement: HTMLElement | null
  rootContainer?: HTMLElement | null
  coverElement?: HTMLElement | null
  onClose: () => void
  dismissOnSiblingOverlay?: boolean
}

export type OverlayAddInput = Omit<OverlayEntry, 'id' | 'zIndex'>
export const BASE_OVERLAY_Z_INDEX = 1000

function targetContainedByOverlayInteractiveTree(
  overlay: OverlayEntry,
  target: EventTarget | null,
): boolean {
  if (!target || !(target instanceof Node)) {
    return false
  }
  if (overlay.element?.contains(target)) {
    return true
  }
  if (overlay.referenceElement?.contains(target)) {
    return true
  }
  return false
}

export const OverlayIgnoreAttribute = 'data-overlay-ignore'

function isEventInsideOverlayIgnoredElement(target: EventTarget | null): boolean {
  if (!(target instanceof Node)) return false
  const element = target instanceof Element ? target : target.parentElement
  return !!element?.closest(`[${OverlayIgnoreAttribute}]`)
}

const [useProvideOverlayStack, useOverlayStack] = createInjectionState(() => {
  const overlays = ref<OverlayEntry[]>([])
  let lastPrimaryPointerDownTarget: EventTarget | null = null

  const handlePointerDownCapture = (event: PointerEvent) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      lastPrimaryPointerDownTarget = null
      return
    }
    lastPrimaryPointerDownTarget = event.target
  }

  const addOverlay = (entry: OverlayAddInput): OverlayEntry => {
    for (const existing of [...overlays.value]) {
      if (!existing.dismissOnSiblingOverlay) {
        continue
      }
      const isAncestorOfNew = !!existing.element && (
        (!!entry.referenceElement && existing.element.contains(entry.referenceElement))
        || (!!entry.element && existing.element.contains(entry.element))
      )
      if (!isAncestorOfNew) {
        existing.onClose()
      }
    }

    const id = Symbol()
    const currentTopZIndex =
      overlays.value.length > 0 ? overlays.value[0].zIndex : BASE_OVERLAY_Z_INDEX - 1
    const dynamicZIndex = currentTopZIndex + 1

    const entryWithZIndex: OverlayEntry = {
      ...entry,
      id,
      zIndex: dynamicZIndex,
    }

    const insertIndex = overlays.value.findIndex(o => o.zIndex < dynamicZIndex)
    if (insertIndex === -1) {
      overlays.value.push(entryWithZIndex)
    } else {
      overlays.value.splice(insertIndex, 0, entryWithZIndex)
    }

    return entryWithZIndex
  }

  const removeOverlay = (id: symbol) => {
    const index = overlays.value.findIndex(o => o.id === id)
    if (index !== -1) {
      overlays.value.splice(index, 1)
    }
  }

  const getTopmostOverlay = computed(() => {
    return overlays.value.length > 0 ? overlays.value[0] : null
  })

  const hideAnchoredOverlaysBelow = (element: HTMLElement): () => void => {
    const owner = overlays.value.find(overlay => overlay.element === element)
    if (!owner) {
      return () => {}
    }
    const hiddenElements = overlays.value
      .filter(overlay => overlay.zIndex < owner.zIndex && overlay.element && overlay.referenceElement && !element.contains(overlay.referenceElement))
      .map(overlay => overlay.element)
    hiddenElements.forEach(hiddenElement => {
      hiddenElement.style.opacity = '0'
      hiddenElement.style.visibility = 'hidden'
    })
    return () => {
      hiddenElements.forEach(hiddenElement => {
        hiddenElement.style.removeProperty('opacity')
        hiddenElement.style.removeProperty('visibility')
      })
    }
  }

  const isClickInsideOverlay = (event: MouseEvent, ownerElement: HTMLElement | null = null): boolean => {
    const path = event.composedPath() as HTMLElement[]
    const clickTarget = event.target as HTMLElement

    for (const overlay of overlays.value) {
      if (ownerElement && overlay.element?.contains(ownerElement)) {
        continue
      }
      if (overlay.element && path.includes(overlay.element)) {
        return true
      }
      if (targetContainedByOverlayInteractiveTree(overlay, lastPrimaryPointerDownTarget)) {
        return true
      }
      if (overlay.referenceElement) {
        if (path.includes(overlay.referenceElement)) return true
        if (overlay.referenceElement.contains(clickTarget)) return true
        
        for (const pathElement of path) {
          if (pathElement instanceof Node && overlay.referenceElement.contains(pathElement)) {
            return true
          }
        }
      }
    }
    return false
  }

  function interactionInsideOverlayWithHigherZIndex(
    overlay: OverlayEntry,
    path: EventTarget[],
    pressTarget: EventTarget | null,
    clickTarget: EventTarget,
  ): boolean {
    for (const o of overlays.value) {
      if (o.zIndex <= overlay.zIndex || !o.element) {
        continue
      }
      if (path.includes(o.element)) {
        return true
      }
      if (pressTarget instanceof Node && o.element.contains(pressTarget)) {
        return true
      }
      if (clickTarget instanceof Node && o.element.contains(clickTarget)) {
        return true
      }
    }
    return false
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (isEventInsideOverlayIgnoredElement(event.target) || isEventInsideOverlayIgnoredElement(lastPrimaryPointerDownTarget)) {
      lastPrimaryPointerDownTarget = null
      return
    }
    const path = event.composedPath() as HTMLElement[]
    const clickTarget = event.target as HTMLElement
    const pressTarget = lastPrimaryPointerDownTarget

    const referencedOverlay = overlays.value.find(overlay => {
      if (!overlay.referenceElement) return false

      if (path.includes(overlay.referenceElement)) return true
      if (overlay.referenceElement.contains(clickTarget)) return true
      if (targetContainedByOverlayInteractiveTree(overlay, pressTarget)) return true

      for (const pathElement of path) {
        if (pathElement instanceof Node && overlay.referenceElement.contains(pathElement)) {
          return true
        }
      }

      return false
    })

    if (referencedOverlay) {
      const clickedInsideOtherOverlay = overlays.value.some(overlay => {
        if (overlay.id === referencedOverlay.id) return false

        if (
          overlay.element &&
          referencedOverlay.referenceElement &&
          overlay.element.contains(referencedOverlay.referenceElement)
        ) {
          return false
        }

        if (overlay.element && path.includes(overlay.element)) {
          return true
        }

        if (overlay.element && pressTarget instanceof Node && overlay.element.contains(pressTarget)) {
          return true
        }

        return false
      })

      if (clickedInsideOtherOverlay) {
        lastPrimaryPointerDownTarget = null
        return
      }

      overlays.value.forEach(overlay => {
        if (overlay.id !== referencedOverlay.id) {
          if (interactionInsideOverlayWithHigherZIndex(overlay, path, pressTarget, clickTarget)) {
            return
          }
          if (targetContainedByOverlayInteractiveTree(overlay, pressTarget)) {
            return
          }
          if (
            overlay.element &&
            referencedOverlay.referenceElement &&
            overlay.element.contains(referencedOverlay.referenceElement)
          ) {
            return
          }
          overlay.onClose()
        }
      })
      lastPrimaryPointerDownTarget = null
      return
    }

    const overlaysToClose: OverlayEntry[] = []

    for (const overlay of overlays.value) {
      const clickedInside =
        !!(overlay.element && path.includes(overlay.element)) ||
        !!(overlay.element && pressTarget instanceof Node && overlay.element.contains(pressTarget))

      if (clickedInside) {
        break
      }
      overlaysToClose.push(overlay)

      // A click on an overlay's own backdrop belongs to that overlay: it closes it and must not
      // fall through to the overlays stacked underneath (e.g. a modal opened from a dropdown)
      const clickedOnCover =
        !!(overlay.coverElement && path.includes(overlay.coverElement)) ||
        !!(overlay.coverElement && pressTarget instanceof Node && overlay.coverElement.contains(pressTarget))
      if (clickedOnCover) {
        break
      }
    }

    overlaysToClose.forEach(overlay => {
      overlay.onClose()
    })
    lastPrimaryPointerDownTarget = null
  }

  useEventListener(document, 'pointerdown', handlePointerDownCapture, {capture: true})
  useEventListener(document, 'click', handleClickOutside, {capture: true})
  useEventListener(document, 'contextmenu', handleClickOutside, {capture: true})

  return {
    overlays,
    addOverlay,
    removeOverlay,
    getTopmostOverlay,
    isClickInsideOverlay,
    hideAnchoredOverlaysBelow,
  }
})

export {useProvideOverlayStack, useOverlayStack}

